import { ChevronRight } from 'lucide-react';
import { Item, User } from '../lib/api';
import { haptic } from '../lib/telegram';
import { PortfolioMiniCard } from './profile/PortfolioMiniCard';

interface HeroCarouselProps {
  title: string;
  items: (Item | User)[];
  onItemClick: (item: Item | User) => void;
  onShowMore?: (sortType?: 'popular' | 'trending' | 'newest') => void;
  type?: 'items' | 'artists';
  sortType?: 'popular' | 'trending' | 'newest';
}

export function HeroCarousel({ title, items, onItemClick, onShowMore, type = 'items', sortType }: HeroCarouselProps) {
  if (items.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4 px-4">
        <h2 className="text-xl font-bold text-white">{title}</h2>
        {onShowMore && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              haptic.light();
              if (onShowMore) {
                onShowMore(sortType);
              }
            }}
            className="flex items-center gap-1 px-3 py-2 rounded-full glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/5 transition-all group flex-shrink-0"
          >
            <span className="text-sm text-white/70 group-hover:text-white transition-colors whitespace-nowrap">
              Більше
            </span>
            <ChevronRight className="w-4 h-4 text-white/50 group-hover:text-splyt-green transition-colors" />
          </button>
        )}
      </div>
      
      <div 
        className="flex gap-4 overflow-x-scroll pb-4 px-4 scrollbar-hide"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {items.map((item) => {
          if (type === 'artists') {
            const artist = item as User;
            return (
              <button
                key={artist.id}
                onClick={() => {
                  haptic.light();
                  onItemClick(artist);
                }}
                className="flex-shrink-0 w-32 flex flex-col items-center gap-3"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="w-32 h-32 rounded-2xl overflow-hidden glass border border-white/10">
                  {/* Show avatar if available, otherwise show initials */}
                  {artist.avatar ? (
                    <img 
                      src={artist.avatar} 
                      alt={artist.first_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <div class="w-full h-full bg-gradient-to-br from-splyt-green/20 to-splyt-purple/20 flex items-center justify-center">
                              <span class="text-3xl font-bold text-white">
                                ${artist.username ? artist.username.charAt(0).toUpperCase() : artist.first_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          `;
                        }
                      }}
                    />
                  ) : (artist.username || artist.first_name) ? (
                    <div className="w-full h-full bg-gradient-to-br from-splyt-green/20 to-splyt-purple/20 flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">
                        {artist.username ? artist.username.charAt(0).toUpperCase() : artist.first_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-splyt-green/20 to-splyt-purple/20" />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-white line-clamp-1">
                    {artist.first_name}
                  </p>
                  {artist.username && (
                    <p className="text-xs text-white/50">@{artist.username}</p>
                  )}
                </div>
              </button>
            );
          }

          const beatItem = item as Item;
          return (
            <div 
              key={beatItem.id} 
              className="flex-shrink-0 w-48"
              style={{ scrollSnapAlign: 'start' }}
            >
              <PortfolioMiniCard
                item={beatItem}
                onClick={() => {
                  haptic.light();
                  onItemClick(beatItem);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
